import React from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/auth-context';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, AlertTriangle } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPage: string;
}

export default function ProtectedRoute({ children, requiredPage }: ProtectedRouteProps) {
  const { canAccessPage, userPermissions, loading, user } = useAuth();
  const [, setLocation] = useLocation();

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-center text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Store intended path for redirect after login
    sessionStorage.setItem('intendedPath', window.location.pathname);
    setLocation('/login');
    return null;
  }

  if (!canAccessPage(requiredPage)) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold">Access Denied</h3>
                  <p>You don't have permission to access this page.</p>
                </div>                
              </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function getRequiredPermissions(page: string): string[] {
  switch (page) {
    case 'videos':
      return ['videoManager', 'administrator'];
    case 'articles':
      return ['articleManager', 'administrator'];
    case 'user-management':
      return ['administrator'];
    case 'categories':
    case 'authors':
    case 'promotions':
    case 'customers':
    case 'settings':
      return ['administrator'];
    default:
      return ['Authentication required'];
  }
}